import { deburr } from "lodash";
import type { IGroupOption, IOption, SelectOptions } from "./types";

type FilterCandidate = {
  label: string;
  value: string;
};

const normalizeSearch = (value: string): string => deburr(value.toLowerCase());

const isGroupOption = (
  option: IOption | IGroupOption,
): option is IGroupOption => {
  return "options" in option && Array.isArray(option.options);
};

export const createFilterOption = (options: SelectOptions) => {
  const normalizedLabelByValue = new Map<string, string>();
  const groupedOptionsByNormalizedGroupLabel = new Map<string, Set<string>>();

  for (const option of options) {
    if (isGroupOption(option)) {
      const normalizedGroupLabel = normalizeSearch(option.label);
      const groupOptions =
        groupedOptionsByNormalizedGroupLabel.get(normalizedGroupLabel) ??
        new Set<string>();

      for (const groupedOption of option.options) {
        normalizedLabelByValue.set(
          groupedOption.value,
          normalizeSearch(groupedOption.label),
        );
        groupOptions.add(groupedOption.value);
      }

      groupedOptionsByNormalizedGroupLabel.set(
        normalizedGroupLabel,
        groupOptions,
      );
      continue;
    }

    normalizedLabelByValue.set(option.value, normalizeSearch(option.label));
  }

  const matchedGroupOptionsBySearch = new Map<string, Set<string>>();

  return ({ label, value }: FilterCandidate, searchPhrase: string) => {
    const normalizedSearchPhrase = normalizeSearch(searchPhrase);
    if (!normalizedSearchPhrase) return true;

    const normalizedLabel =
      normalizedLabelByValue.get(value) ?? normalizeSearch(label);
    if (normalizedLabel.includes(normalizedSearchPhrase)) return true;

    const matchedOptionsInGroups = matchedGroupOptionsBySearch.get(
      normalizedSearchPhrase,
    );
    if (matchedOptionsInGroups) {
      return matchedOptionsInGroups.has(value);
    }

    const matchedGroupedOptions = new Set<string>();
    for (const [
      normalizedGroupLabel,
      groupedOptionValues,
    ] of groupedOptionsByNormalizedGroupLabel) {
      if (!normalizedGroupLabel.includes(normalizedSearchPhrase)) continue;

      for (const groupedOptionValue of groupedOptionValues) {
        matchedGroupedOptions.add(groupedOptionValue);
      }
    }

    matchedGroupOptionsBySearch.set(
      normalizedSearchPhrase,
      matchedGroupedOptions,
    );

    return matchedGroupedOptions.has(value);
  };
};

export const filterOption = (
  candidate: FilterCandidate,
  searchPhrase: string,
  options: SelectOptions,
) => {
  return createFilterOption(options)(candidate, searchPhrase);
};
